import React, { useState } from 'react';
import { Link2, Check, X } from 'lucide-react';
import { ABLE_COLORS } from '../constants';

/**
 * EntitySuggestionPrompt - Subtle inline chip for entity matching
 * 
 * Compact design that doesn't interrupt the conversation flow.
 */
function EntitySuggestionPrompt({ 
  mention, 
  entityName, 
  score, 
  onAccept, 
  onReject,
  onDismiss 
}) {
  const [loading, setLoading] = useState(false);
  const [showOptions, setShowOptions] = useState(false);

  const handleAccept = async (permanent) => {
    setLoading(true);
    try {
      await onAccept(permanent);
    } catch (error) {
      console.error('Accept error:', error);
    } finally {
      setLoading(false);
      setShowOptions(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      await onReject();
    } catch (error) {
      console.error('Reject error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      background: `${ABLE_COLORS.primary}10`,
      border: `1px solid ${ABLE_COLORS.primary}30`,
      borderRadius: '20px',
      padding: '6px 12px',
      marginBottom: '8px',
      fontSize: '13px',
      color: ABLE_COLORS.primary,
      position: 'relative'
    }}>
      <Link2 size={14} />
      
      <span style={{ opacity: 0.8 }}>
        Did you mean <strong>{entityName}</strong>?
      </span>

      {/* Action buttons */}
      {!showOptions ? (
        <div style={{ display: 'flex', gap: '4px', marginLeft: '4px' }}>
          <button
            onClick={() => setShowOptions(true)}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              background: ABLE_COLORS.primary,
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
            title="Yes, link it"
          >
            <Check size={14} />
          </button>
          
          <button
            onClick={handleReject}
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              background: 'transparent',
              color: '#999',
              border: '1px solid #ddd',
              borderRadius: '50%',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
            title="No, dismiss"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        /* Options dropdown */
        <div style={{
          display: 'flex',
          gap: '6px',
          marginLeft: '4px'
        }}>
          <button
            onClick={() => handleAccept(true)}
            disabled={loading}
            style={{
              padding: '4px 10px',
              background: ABLE_COLORS.primary,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            Always
          </button>
          <button
            onClick={() => handleAccept(false)}
            disabled={loading}
            style={{
              padding: '4px 10px',
              background: 'white',
              color: ABLE_COLORS.primary,
              border: `1px solid ${ABLE_COLORS.primary}`,
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '500',
              cursor: loading ? 'wait' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            Just now
          </button>
          <button
            onClick={() => setShowOptions(false)}
            disabled={loading}
            style={{
              padding: '4px 8px',
              background: 'transparent',
              color: '#999',
              border: 'none',
              fontSize: '12px',
              cursor: 'pointer'
            }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
}

export default EntitySuggestionPrompt;
